// src/features/records/services/interactions/marks.js

import { db } from '../../../../firebase';
import {
  collection,
  deleteDoc,
  doc,
  documentId,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  startAfter
} from 'firebase/firestore';
import { requireUid } from '../utils/auth';

const normalizeMarkSnapshot = (snapshot) => {
  if (!snapshot?.exists()) {
    return null;
  }

  const data = snapshot.data() || {};

  return {
    id: snapshot.id,
    recordId: data.recordId || snapshot.id,
    markedAt: data.markedAt || null
  };
};

function validateRecordId(recordId) {
  if (!recordId || typeof recordId !== 'string') {
    throw new Error('recordId is required');
  }
}

function getMarkRef(userId, recordId) {
  return doc(db, 'users', userId, 'markedRecords', recordId);
}

/**
 * Create a mark edge for the current user (idempotent).
 */
export async function mark(recordId) {
  const userId = requireUid();
  validateRecordId(recordId);

  const markRef = getMarkRef(userId, recordId);

  await runTransaction(db, async (transaction) => {
    const existing = await transaction.get(markRef);
    if (existing.exists()) {
      return;
    }

    transaction.set(markRef, {
      recordId,
      markedAt: serverTimestamp()
    });
  });
}

/**
 * Remove a mark edge for the current user.
 */
export async function unmark(recordId) {
  const userId = requireUid();
  validateRecordId(recordId);

  const markRef = getMarkRef(userId, recordId);
  await deleteDoc(markRef);
}

/**
 * Subscribe to mark status for the current user and record.
 */
export function subscribeMarked(recordId, onChange, onError) {
  const userId = requireUid();
  validateRecordId(recordId);

  if (typeof onChange !== 'function') {
    return () => {};
  }

  const markRef = getMarkRef(userId, recordId);

  return onSnapshot(
    markRef,
    (snapshot) => {
      const markEntry = normalizeMarkSnapshot(snapshot);
      onChange({
        marked: Boolean(markEntry),
        recordId,
        markedAt: markEntry?.markedAt ?? null,
        mark: markEntry
      });
    },
    (error) => {
      if (typeof onError === 'function') {
        onError(error);
      }
    }
  );
}

/**
 * List marked records for the current user.
 */
export async function listMarked({ pageSize = 30, cursor = null } = {}) {
  const userId = requireUid();
  const safePageSize = Number.isFinite(pageSize)
    ? Math.max(1, Math.floor(pageSize))
    : 30;

  const marksRef = collection(db, 'users', userId, 'markedRecords');
  const clauses = [
    orderBy('markedAt', 'desc'),
    orderBy(documentId(), 'desc'),
    limit(safePageSize)
  ];

  if (cursor) {
    clauses.splice(2, 0, startAfter(cursor));
  }

  const q = query(marksRef, ...clauses);
  const snapshot = await getDocs(q);

  const marks = snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data()
  }));

  return {
    marks,
    lastCursor: snapshot.docs[snapshot.docs.length - 1] ?? null,
    hasMore: snapshot.size === safePageSize
  };
}
